import React, { useContext, useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button as ButtonBase, Grid as GridBase } from "@mui/material";

import { GlobalContext } from "@/helpers/contexts/contexts";
import ImageRPBase from "@/components/ImageRP";
import { getFormattedCampaignDate } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import RpImageTextInlineBase from "@/components/RpImageTextInline";

import { PauseLocationAddIcon as PauseLocationAddIconBase } from "@/features/campaigns/digital/pause-campaign";
import TagChipContainerBase from "@/components/muiCustom/chip/TagChip";
import { CAMPAIGN_CONST, USER_ROLE } from "@/config/constants";
import LearnMoreButtonBase from "@/components/muiCustom/buttons/LearnMoreButton";

type CampaignReportDetailProps = {
  campaign?: any;
  values?: any;
};

const Button = ButtonBase as React.ComponentType<any>;
const Grid = GridBase as React.ComponentType<any>;
const ImageRP = ImageRPBase as React.ComponentType<any>;
const RpImageTextInline = RpImageTextInlineBase as React.ComponentType<any>;
const TagChipContainer = TagChipContainerBase as React.ComponentType<any>;
const LearnMoreButton = LearnMoreButtonBase as React.ComponentType<any>;
const PauseLocationAddIcon =
  PauseLocationAddIconBase as React.ComponentType<any>;
const getFormattedCampaignDateForReport = getFormattedCampaignDate as (
  ..._args: any[]
) => string;

const Detail = ({
  title = "",
  children,
  image = "",
  svgIcon = false,
}: {
  title?: string;
  children?: ReactNode;
  image?: string;
  svgIcon?: string | boolean;
}) => {
  return (
    <div
      className="col-lg-3 col-md-4 col-sm-6 mb-2"
      style={{ color: "var(--palette-text-primary)" }}
    >
      <RpImageTextInline
        image={image}
        svgIcon={svgIcon}
        imageProps={{ color: "primary" }}
        text={
          <>
            <div className="title">{title}</div>
            {children}
          </>
        }
        alignCenter={false}
      />
    </div>
  );
};

const CampaignReportDetail = ({
  campaign,
  values = {},
}: CampaignReportDetailProps) => {
  const { loggedInUserRole, loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as any;
  const formattedCampaignDate = useMemo(() => {
    return getFormattedCampaignDateForReport(campaign, {
      loggedInUserRole,
      selectedBrand,
    });
  }, [campaign, loggedInUserRole, selectedBrand]);

  const channelName = useMemo(() => {
    // const { campaignChannelName } = campaign;
    if (campaign?.campaignChannelName?.[0]?.channel_name) {
      return campaign.campaignChannelName[0].channel_name.replace(/,/g, ", ");
    }
    return "N/A";
  }, [campaign]);

  const groupName = useMemo(() => {
    const { group_id } = values;
    // const { group_name, group_list } = campaign;

    const group = campaign?.group_list?.find(
      (g) => String(g.groups_id) === String(group_id)
    );
    if (campaign?.group_name) {
      return campaign?.group_name?.[0]?.group_name;
    } else if (group) {
      return group.group_name;
    }
    return "All";
  }, [campaign, values]);

  const renderBudget = () => {
    // const { brand_contribution } = campaign;
    if (!campaign?.brand_contribution) {
      return (
        <Detail title="Total Campaign Budget" svgIcon="money">
          <div className="value main">
            {campaign?.budgetResult[0]?.total_budget ? (
              <CurrencyFormateText
                value={campaign?.budgetResult?.[0]?.total_budget}
              />
            ) : (
              "N/A"
            )}
          </div>
        </Detail>
      );
    } else {
      return (
        // <Detail className="col-lg-6">
        //   <div className="row">
        //   </div>
        // </Detail>
        <>
          <Detail title="Local Budget" svgIcon="money">
            <div className="value">
              {campaign?.budgetResult[0]?.local_budget ||
              Number(campaign?.budgetResult[0]?.local_budget) === 0 ? (
                <CurrencyFormateText
                  value={campaign?.budgetResult?.[0]?.local_budget}
                />
              ) : (
                "N/A"
              )}
            </div>
          </Detail>
          <Detail title="Brand Match" svgIcon="money">
            <div className="value">
              {campaign?.budgetResult[0]?.brand_match ||
              Number(campaign?.budgetResult[0]?.brand_match) === 0 ? (
                <CurrencyFormateText
                  value={campaign?.budgetResult?.[0]?.brand_match}
                />
              ) : (
                "N/A"
              )}
            </div>
          </Detail>
          <Detail title="Total Budget" svgIcon="money">
            <div className="value main">
              {campaign?.budgetResult[0]?.total_budget ||
              Number(campaign?.budgetResult[0]?.total_budget) === 0 ? (
                <CurrencyFormateText
                  value={campaign?.budgetResult?.[0]?.total_budget}
                />
              ) : (
                "N/A"
              )}
            </div>
          </Detail>
        </>
      );
    }
  };

  const actionButtons = useMemo(() => {
    if (!campaign) return null;

    const hasBoost =
      loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
      !!campaign?.is_campaign_boost &&
      selectedBrand &&
      selectedBrand?.status &&
      (campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
        campaign?.status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID);

    const hasExtend =
      campaign?.can_participant_extend_campaign &&
      campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID;

    const hasPause =
      campaign &&
      selectedBrand &&
      selectedBrand?.status &&
      campaign.pause_campaign_available;

    const buttonStyle = {
      fontSize: 9,
      padding: "4px 16px",
      minWidth: "auto",
      height: "24px",
      borderRadius: "var(--radius-control)",
      flexShrink: 0,
    };

    const hideActions = !hasBoost && !hasExtend && !hasPause;

    const shouldShowActions =
      loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME || !hideActions;

    if (!shouldShowActions) {
      return null;
    }

    return (
      <Detail title="Actions" svgIcon="down_arrow">
        <div className="value mt-1">
          <Grid
            container
            alignItems="center"
            justifyContent={{
              md: "flex-start",
            }}
            spacing={0.9}
          >
            {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
              <Grid size={{ md: "auto" }}>
                <LearnMoreButton
                  to={{
                    pathname: `/dashboard/learn-more/digital-campaign/${campaign?.id}`,
                    state: { campaignId: campaign?.id, from: "/dashboard" },
                  }}
                  icon
                  className="mb-1"
                  style={{ width: "24px", height: "24px" }}
                />
              </Grid>
            )}
            {hasBoost ? (
              <Grid size={{ md: "auto" }}>
                <Button
                  component={Link}
                  to={{
                    pathname: `/adjust-budget/${campaign?.id}`,
                    state: { campaignId: campaign?.id, from: "/dashboard" },
                  }}
                  size="small"
                  variant="contained"
                  color="warning"
                  className="text-white"
                  style={{
                    ...buttonStyle,
                    backgroundColor: "var(--status-warning)",
                  }}
                  disableElevation
                >
                  Boost
                </Button>
              </Grid>
            ) : null}
            {hasExtend ? (
              <Grid size={{ md: "auto" }}>
                <Button
                  component={Link}
                  to={`/extend-campaign/${campaign?.id}`}
                  size="small"
                  variant="contained"
                  color="warning"
                  className="text-white"
                  style={{
                    ...buttonStyle,
                    backgroundColor: "var(--primary-base)",
                  }}
                  disableElevation
                >
                  Extend
                </Button>
              </Grid>
            ) : null}
            {hasPause ? (
              <Grid size={{ md: "auto" }}>
                <PauseLocationAddIcon
                  campaignId={campaign?.id}
                  campaign={campaign}
                  style={
                    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME
                      ? { width: "24px", height: "24px" }
                      : {}
                  }
                  popoverPosition="bottom-right"
                  className={
                    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME
                      ? "mt-1"
                      : "mb-1"
                  }
                />
              </Grid>
            ) : null}
          </Grid>
        </div>
      </Detail>
    );
  }, [campaign, loggedInUserRole, selectedBrand]);

  const renderTags = () => {
    return (
      <>
        {campaign?.tags && campaign?.tags.length > 0 && (
          <div className="col p-0 ml-3">
            <RpImageTextInline
              svgIcon="tags"
              imageProps={{ color: "primary" }}
              text={<div className="title">Tags</div>}
              alignCenter={false}
            />
            {/* <Detail title="Brand Match" svgIcon="money" className="mb-0" /> */}
            {campaign?.tags && campaign?.tags.length > 0 && (
              <div className="col mb-3 ml-1">
                <TagChipContainer tags={campaign?.tags || []} />
              </div>
            )}
          </div>
        )}
      </>
    );
  };
  const renderDetails = () => (
    <div className="row h-100">
      {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Name" svgIcon="person">
          <div className="value">
            {`${loggedInUser.first_name} ${loggedInUser.last_name}`}
          </div>
        </Detail>
      )}
      <Detail title="Campaign Name" svgIcon="campaign">
        <div className="value main">{campaign?.campaign_name}</div>
      </Detail>
      <Detail title="Unique ID" svgIcon="unique_id">
        <div className="value us-all">
          {campaign?.system_generated_campaign_id}
        </div>
      </Detail>
      <Detail title="Campaign Date" svgIcon="campaign_calender">
        <div className="value">{formattedCampaignDate}</div>
      </Detail>
      <Detail title="Channel" svgIcon="channel">
        <div className="value" title={channelName}>
          {channelName}
        </div>
      </Detail>

      {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Participants" svgIcon="users">
          <div className="value">
            {campaign?.campaignDetails?.[0]?.participant_count || 0}
          </div>
        </Detail>
      )}
      <Detail title="Locations" svgIcon="location_mark">
        <div className="value">
          {campaign?.campaignDetails?.[0]?.location_count || 0}
        </div>
      </Detail>
      {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Group(s)" svgIcon="home">
          <div className="value main">{groupName}</div>
        </Detail>
      )}
      {renderBudget()}
      {actionButtons}
      {renderTags()}
    </div>
  );

  return (
    <div className="row align-items-stretch">
      {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
        <div className="col-md-3 col-lg-2 text-center d-none d-md-block">
          <ImageRP
            src={loggedInUser.profile_name}
            type="PROFILE"
            style={{ maxHeight: 130 }}
            className="img-fluid img-thumbnail"
            loading="eager"
          />
        </div>
      )}
      <div className="col">{renderDetails()}</div>
    </div>
  );
};

export default CampaignReportDetail;
